import React, { useEffect, useState } from "react"
import * as Yup from "yup"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import Split from "react-split-it"
import { Form, Formik, FormikHelpers } from "formik"
import MyEditorField from "@common-utils/fields/MyEditorField"
import { toast } from "react-toastify"
import { IAddMessageRepondeurRequestModel, ICandidatAddSuiviMessageRepondeurlFormModel } from "./core/_models"
import { addSuiviMessageRepondeur } from "./core/_requests"
import MyDateDayMonthYearHoursMinutesField from "@common-utils/fields/MyDateDayMonthYearHoursMinutesField"
import moment from "moment"
import { AxiosError } from "axios"
import { CandidatureModel, IHttpErrorResponseModel } from "@common-models/*"
import { CONST_CANDIDAT_TYPE_CS, CONST_CANDIDAT_TYPE_POSTULANT, CONST_CANDIDAT_TYPE_SAISI, CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR } from "@common-constants/*"
import clsx from "clsx"
import { getCandidatures } from "../core/_requests"

// --- Hook pour le design responsive ---
const useMediaQuery = (query: string) => {
     const [matches, setMatches] = useState(false)
     useEffect(() => {
          const media = window.matchMedia(query)
          if (media.matches !== matches) setMatches(media.matches)
          const listener = () => setMatches(media.matches)
          window.addEventListener("resize", listener)
          return () => window.removeEventListener("resize", listener)
     }, [matches, query])
     return matches
}

const CandidatAddSuiviMessageRepondeurForm = ({ candidat, curriculumGoogleDriveUrl, handleSubmitCallBack }: ICandidatAddSuiviMessageRepondeurlFormModel) => {
     const isXlScreen = useMediaQuery("(min-width: 1200px)")
     const [activeTab, setActiveTab] = useState<"form" | "cv">("form")

     const [candidatures, setCandidatures] = useState<CandidatureModel[]>([])
     const [isLoadingCandidatures, setIsLoadingCandidatures] = useState(false)

     // --- Récupération des candidatures si le candidat est un postulant ---
     useEffect(() => {
          if (candidat.type === CONST_CANDIDAT_TYPE_POSTULANT && candidat.id) {
               setIsLoadingCandidatures(true)
               getCandidatures(candidat.id as number)
                    .then(r => setCandidatures(r.data))
                    .catch(() => toast.error("Impossible de récupérer les offres du candidat"))
                    .finally(() => setIsLoadingCandidatures(false))
          }
     }, [candidat.id, candidat.type])

     // --- Composant interne du formulaire ---
     const FormSuivi = () => {
          const schema = Yup.object().shape({
               notes: Yup.string().label(`"Notes"`),
               voulezVousProgrammerUneDateDeRelance: Yup.boolean(),
               dateRelance: Yup.string()
                    .when("voulezVousProgrammerUneDateDeRelance", {
                         is: true,
                         then: schema => schema.required("La date de relance est requise"),
                    })
                    .label(`"Date de relance"`),
               annonce: Yup.number(),
          })

          interface IFormValuesModel {
               notes?: string
               voulezVousProgrammerUneDateDeRelance: boolean
               dateRelance?: string
               annonce?: number
               candidatType: string
          }

          const initialValues: IFormValuesModel = {
               notes: "",
               voulezVousProgrammerUneDateDeRelance: true,
               dateRelance: moment().add(7, "days").hour(9).minute(0).toString(),
               // Sélection automatique si une seule candidature est trouvée
               annonce: candidatures.length === 1 ? (candidatures[0].annonce?.id as number) : undefined,
               candidatType: candidat.type || "",
          }

          function handleSubmit(values: IFormValuesModel, helpers: FormikHelpers<IFormValuesModel>) {
               helpers.setStatus(null)
               const requestModel: IAddMessageRepondeurRequestModel = {
                    notes: values.notes,
                    dateRelance: values.voulezVousProgrammerUneDateDeRelance ? values.dateRelance : undefined,
                    annonce: values.annonce,
               }
               addSuiviMessageRepondeur(candidat.id, requestModel)
                    .then(() => {
                         toast.success("Message répondeur enregistré.")
                         handleSubmitCallBack()
                    })
                    .catch((e: AxiosError) => {
                         const error: IHttpErrorResponseModel = e.response?.data
                         if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                              for (const key in error.errors) helpers.setFieldError(key, error.errors[key])
                         }
                         helpers.setStatus(error?.detail)
                         toast.error(error?.detail || "Une erreur est survenue")
                         helpers.setSubmitting(false)
                    })
          }

          return (
               <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={schema}
                    enableReinitialize={true} // Permet de mettre à jour le formulaire quand les candidatures arrivent
               >
                    {helpers => (
                         <Form noValidate autoComplete="off" className="d-flex flex-column h-100">
                              <div className="card card-flush h-100 shadow-none bg-transparent">
                                   <div className="card-body p-0 d-flex flex-column gap-6">
                                        {/* --- SECTION CONTEXTE --- */}
                                        <div className="rounded border border-dashed border-gray-300 p-4 bg-light-secondary bg-opacity-20">
                                             <label className="fs-7 fw-boldest text-uppercase mb-3 text-gray-600 d-block">Contexte du candidat</label>

                                             {candidat.type === CONST_CANDIDAT_TYPE_POSTULANT && (
                                                  <div className="d-flex flex-column gap-2">
                                                       {/* Gestion de la pluralisation correcte */}
                                                       <span className="fs-8 fw-bold text-gray-500">
                                                            Le candidat a postulé à {candidatures.length} {candidatures.length > 1 ? "offres" : "offre"}.
                                                       </span>
                                                       {isLoadingCandidatures ? (
                                                            <div className="py-2">
                                                                 <MySimpleSpinner size="sm" />
                                                            </div>
                                                       ) : (
                                                            <select
                                                                 className={clsx("form-select form-select-sm form-select-solid", helpers.errors.annonce && "is-invalid")}
                                                                 value={helpers.values.annonce}
                                                                 onChange={e => helpers.setFieldValue("annonce", e.target.value)}
                                                            >
                                                                 <option value="">Sélectionner l'offre concernée...</option>
                                                                 {candidatures.map(c => (
                                                                      <option key={c.annonce?.id} value={c.annonce?.id}>
                                                                           {c.annonce?.intitule || "Sans titre"} — {moment(c.date).format("DD/MM/YYYY")}
                                                                      </option>
                                                                 ))}
                                                            </select>
                                                       )}
                                                  </div>
                                             )}

                                             {candidat.type === CONST_CANDIDAT_TYPE_CS && (
                                                  <div className="notice d-flex bg-light-primary rounded border-primary border border-dashed p-3">
                                                       <i className="fas fa-info-circle text-primary fs-4 me-3"></i>
                                                       <div className="fs-7 fw-bold text-primary">Le candidat a postulé en candidature spontanée.</div>
                                                  </div>
                                             )}

                                             {candidat.type === CONST_CANDIDAT_TYPE_SAISI && (
                                                  <div className="notice d-flex bg-light-info rounded border-info border border-dashed p-3">
                                                       <i className="fas fa-user-edit text-info fs-4 me-3"></i>
                                                       <div className="fs-7 fw-bold text-info">Le candidat a été ajouté manuellement par le staff et n'est lié à aucune offre.</div>
                                                  </div>
                                             )}
                                        </div>

                                        {/* Header Suivi */}
                                        <div className="d-flex align-items-center mb-2">
                                             <div className="symbol symbol-40px me-3">
                                                  <div className="symbol-label bg-light-warning">
                                                       <i className="fas fa-voicemail text-warning fs-2"></i>
                                                  </div>
                                             </div>
                                             <div>
                                                  <div className="fs-6 fw-bold text-gray-800">Suivi Message Vocal</div>
                                                  <div className="fs-7 text-muted">Ajouter une note rapide suite à votre appel.</div>
                                             </div>
                                        </div>

                                        {/* Éditeur de texte */}
                                        <div className="d-flex flex-column">
                                             <label className="fs-6 fw-bold mb-2 text-gray-700">Contenu de la note</label>
                                             <div className="bg-white rounded border border-gray-300">
                                                  <MyEditorField
                                                       value={helpers.values.notes}
                                                       height={200}
                                                       onChange={val => helpers.setFieldValue("notes", val)}
                                                       placeholder={"Ex: J'ai laissé un message concernant l'offre..."}
                                                  />
                                             </div>
                                        </div>

                                        {/* Planification de relance */}
                                        <div
                                             className={clsx(
                                                  "rounded border border-dashed p-6 transition-all",
                                                  helpers.values.voulezVousProgrammerUneDateDeRelance ? "bg-light-primary border-primary" : "bg-light border-gray-400"
                                             )}
                                        >
                                             <div className="d-flex align-items-center justify-content-between mb-0">
                                                  <div className="d-flex align-items-center">
                                                       <i
                                                            className={clsx(
                                                                 "fas fs-3 me-3",
                                                                 helpers.values.voulezVousProgrammerUneDateDeRelance
                                                                      ? "fa-calendar-check text-primary"
                                                                      : "fa-calendar text-gray-400"
                                                            )}
                                                       ></i>
                                                       <div className="d-flex flex-column">
                                                            <span
                                                                 className={clsx(
                                                                      "fw-bold fs-6",
                                                                      helpers.values.voulezVousProgrammerUneDateDeRelance ? "text-gray-900" : "text-gray-600"
                                                                 )}
                                                            >
                                                                 Planifier une relance ?
                                                            </span>
                                                            <span className="text-muted fs-8">Ajoutera un rappel dans votre agenda Google.</span>
                                                       </div>
                                                  </div>
                                                  <div className="form-check form-switch form-check-custom form-check-solid">
                                                       <input
                                                            className="form-check-input h-25px w-45px cursor-pointer"
                                                            type="checkbox"
                                                            checked={helpers.values.voulezVousProgrammerUneDateDeRelance}
                                                            onChange={e => helpers.setFieldValue("voulezVousProgrammerUneDateDeRelance", e.target.checked)}
                                                       />
                                                  </div>
                                             </div>

                                             {helpers.values.voulezVousProgrammerUneDateDeRelance && (
                                                  <div className="mt-4 animate__animated animate__fadeIn">
                                                       <label className="fs-7 fw-semibold mb-2 text-gray-600">Date et heure du rappel :</label>
                                                       <div className="bg-white rounded">
                                                            <MyDateDayMonthYearHoursMinutesField
                                                                 name={"dateRelance"}
                                                                 isInvalid={!!helpers.errors.dateRelance}
                                                                 onChange={val => helpers.setFieldValue("dateRelance", val)}
                                                                 value={helpers.values.dateRelance}
                                                                 placeholder={"Date de relance"}
                                                            />
                                                       </div>
                                                  </div>
                                             )}
                                        </div>
                                   </div>

                                   <div className="card-footer p-0 pt-6 mt-auto">
                                        <button className="btn btn-primary w-100 py-3 fs-6 fw-bold" type="submit" disabled={helpers.isSubmitting}>
                                             {!helpers.isSubmitting ? (
                                                  <>
                                                       {" "}
                                                       <i className="fas fa-paper-plane me-2"></i> Enregistrer le message{" "}
                                                  </>
                                             ) : (
                                                  <MySimpleSpinner size={"sm"} text="Enregistrement..." />
                                             )}
                                        </button>
                                   </div>
                              </div>
                         </Form>
                    )}
               </Formik>
          )
     }

     const CvViewer = () => (
          <div className="h-100 w-100 bg-gray-100 rounded border border-gray-200 overflow-hidden shadow-sm">
               <iframe title="CV Viewer" allowFullScreen={true} src={curriculumGoogleDriveUrl} style={{ height: "100%", width: "100%", border: "none" }} />
          </div>
     )

     if (isXlScreen) {
          return (
               <div className="h-100 w-100 overflow-hidden">
                    <Split sizes={[40, 60]} direction="horizontal" gutterSize={10} className="d-flex h-100">
                         <div className="h-100 pe-4 overflow-auto custom-scroll" style={{ maxHeight: "85vh" }}>
                              <div className="p-2 h-100">
                                   {" "}
                                   <FormSuivi />{" "}
                              </div>
                         </div>
                         <div className="h-100 ps-2" style={{ maxHeight: "85vh" }}>
                              {" "}
                              <CvViewer />{" "}
                         </div>
                    </Split>
               </div>
          )
     }

     return (
          <div className="d-flex flex-column h-100">
               <div className="d-flex justify-content-center mb-6">
                    <ul className="nav nav-pills nav-pills-custom border border-gray-300 rounded p-1">
                         <li className="nav-item">
                              <button className={clsx("nav-link btn btn-sm fw-bold px-6", activeTab === "form" && "active btn-active-dark")} onClick={() => setActiveTab("form")}>
                                   <i className="fas fa-edit me-2"></i> Formulaire
                              </button>
                         </li>
                         <li className="nav-item">
                              <button className={clsx("nav-link btn btn-sm fw-bold px-6", activeTab === "cv" && "active btn-active-dark")} onClick={() => setActiveTab("cv")}>
                                   <i className="fas fa-file-pdf me-2"></i> Voir le CV
                              </button>
                         </li>
                    </ul>
               </div>
               <div className="flex-grow-1 overflow-auto custom-scroll pb-4">
                    {activeTab === "form" ? (
                         <div className="container-fluid p-1">
                              {" "}
                              <FormSuivi />{" "}
                         </div>
                    ) : (
                         <div className="h-100" style={{ minHeight: "60vh" }}>
                              {" "}
                              <CvViewer />{" "}
                         </div>
                    )}
               </div>
          </div>
     )
}

export default CandidatAddSuiviMessageRepondeurForm
